import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { ArrowLeft, MapPin, Clock, Briefcase, MessageCircle, CheckCircle, Heart, HandHeart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ShareButtons from '../components/ShareButtons';
import ImageLightbox from '../components/ImageLightbox';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TimeOfferDetail = () => {
  const { id } = useParams();
  const { isAuthenticated, user, token } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxIdx, setLightboxIdx] = useState(-1);
  const it = language === 'it';

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/time/offers/${id}/detail`);
        setOffer(res.data);
        document.title = res.data.title + ' - Dona il tuo Tempo Moneyfor';
      } catch { navigate('/donate-time'); }
      finally { setLoading(false); }
    };
    fetch();
    return () => { document.title = 'Moneyfor'; };
  }, [id]);

  const handleChat = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/time/offers/${id}/contact`, {}, { headers: { Authorization: `Bearer ${token}` } });
      navigate('/chat', { state: { conversationId: res.data.conversation_id } });
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  if (loading) return <div className="min-h-screen bg-stone-100 dark:bg-stone-950"><Navbar /><div className="flex justify-center py-20"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-700" /></div></div>;
  if (!offer) return null;

  const isOffer = offer.type === 'offer';
  const isOwner = user?.user_id === offer.user_id;
  const shareUrl = `${API_URL}/api/share/time/${id}`;

  return (
    <div className="min-h-screen bg-stone-100 dark:bg-stone-950 overflow-y-auto">
      <Navbar />
      {lightboxIdx >= 0 && <ImageLightbox images={offer.images} initialIndex={lightboxIdx} onClose={() => setLightboxIdx(-1)} />}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-16">
        <Link to="/donate-time" className="inline-flex items-center gap-2 text-stone-500 hover:text-stone-700 mb-6 text-sm">
          <ArrowLeft className="h-4 w-4" />{it ? 'Torna a Dona Tempo' : 'Back to Donate Time'}
        </Link>

        <div className="bg-white dark:bg-stone-800 rounded-2xl shadow-sm border border-stone-200 dark:border-stone-700 overflow-hidden">
          {offer.images?.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
              {offer.images.map((img, i) => (
                <div key={i} className={`${i === 0 && offer.images.length > 1 ? 'md:row-span-2' : ''} overflow-hidden cursor-pointer`}
                  onClick={() => setLightboxIdx(i)}>
                  <img src={img} alt={offer.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" style={{ minHeight: i === 0 ? '300px' : '150px', maxHeight: i === 0 ? '500px' : '250px' }} />
                </div>
              ))}
            </div>
          )}

          <div className="p-6 md:p-8">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <Badge className={isOffer ? 'bg-violet-100 text-violet-700' : 'bg-rose-100 text-rose-700'}>
                {isOffer ? (it ? 'Offro tempo' : 'Offering time') : (it ? 'Cerco aiuto' : 'Need help')}
              </Badge>
              <Badge variant="secondary">{offer.category}</Badge>
              {offer.city && <Badge variant="outline"><MapPin className="h-3 w-3 mr-1" />{offer.city}</Badge>}
            </div>

            <h1 className="font-heading text-2xl md:text-3xl font-bold text-stone-900 dark:text-white mb-4" data-testid="time-detail-title">{offer.title}</h1>
            <p className="text-stone-600 dark:text-stone-400 leading-relaxed mb-6 whitespace-pre-wrap">{offer.description}</p>

            {offer.skills && (
              <div className="flex items-center gap-2 text-sm text-stone-500 mb-3 p-3 bg-stone-50 dark:bg-stone-700 rounded-xl">
                <Briefcase className="h-4 w-4 shrink-0" /><span>{offer.skills}</span>
              </div>
            )}
            {offer.availability && (
              <div className="flex items-center gap-2 text-sm text-stone-500 mb-6 p-3 bg-stone-50 dark:bg-stone-700 rounded-xl">
                <Clock className="h-4 w-4 shrink-0" /><span>{offer.availability}</span>
              </div>
            )}

            <div className="flex items-center justify-between pt-4 border-t border-stone-200 dark:border-stone-700">
              <Link to={`/profile/${offer.user_id}`} className="flex items-center gap-3">
                <Avatar className="h-10 w-10">
                  <AvatarImage src={offer.user_picture || `https://api.dicebear.com/9.x/lorelei/svg?seed=${offer.user_id}`} />
                  <AvatarFallback>{offer.user_name?.[0]}</AvatarFallback>
                </Avatar>
                <p className="font-semibold text-stone-900 dark:text-white flex items-center gap-1.5">{offer.user_name} {offer.is_verified && <CheckCircle className="h-4 w-4 text-blue-500" />}</p>
              </Link>
              <div className="flex items-center gap-3">
                <ShareButtons url={shareUrl} title={offer.title} description={offer.description} language={language} />
                {isAuthenticated && !isOwner && (
                  <Button onClick={handleChat} className="bg-violet-700 hover:bg-violet-800 text-white rounded-full px-6" data-testid="chat-with-owner">
                    <MessageCircle className="h-4 w-4 mr-2" />{it ? 'Chatta' : 'Chat'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default TimeOfferDetail;
